import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { normalizeFromEndpoint } from '../../../utils/endpointUtils';

const EmailConfig = ({
  config,
  onConfigChange,
  communicationEndpoints,
  onEndpointChange,
  onDelete,
}) => {
  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    onConfigChange({
      ...config,
      [field]: value,
    });
  };

  const handleEndpointChange = (event) => {
    const normalizedEndpoint = normalizeFromEndpoint(event.target.value);
    onEndpointChange(normalizedEndpoint);
  };

  const handleAttachmentDelete = (index) => {
    const newAttachments = [...config.attachments];
    newAttachments.splice(index, 1);
    onConfigChange({
      ...config,
      attachments: newAttachments,
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Email Configuration
      </Typography>

      <FormControl fullWidth margin="normal">
        <InputLabel>From Email</InputLabel>
        <Select
          value={config.from_endpoint || ''}
          onChange={handleEndpointChange}
          label="From Email"
        >
          {communicationEndpoints
            .filter(endpoint => endpoint.type === 'email')
            .map(endpoint => (
              <MenuItem key={endpoint.id} value={endpoint.id}>
                {endpoint.label}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        margin="normal"
        label="From Name"
        value={config.from_name || ''}
        onChange={handleConfigChange('from_name')}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Reply To"
        value={config.reply_to || ''}
        onChange={handleConfigChange('reply_to')}
      />

      <FormControl fullWidth margin="normal">
        <InputLabel>Priority</InputLabel>
        <Select
          value={config.priority || 'normal'}
          onChange={handleConfigChange('priority')}
          label="Priority"
        >
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="normal">Normal</MenuItem>
          <MenuItem value="low">Low</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={config.track_delivery ?? true}
              onChange={handleConfigChange('track_delivery')}
            />
          }
          label="Track Delivery"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={config.track_replies ?? true}
              onChange={handleConfigChange('track_replies')}
            />
          }
          label="Track Replies"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={config.track_opens ?? true}
              onChange={handleConfigChange('track_opens')}
            />
          }
          label="Track Opens"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={config.track_clicks ?? true}
              onChange={handleConfigChange('track_clicks')}
            />
          }
          label="Track Clicks"
        />
      </Box>

      {config.attachments && config.attachments.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Attachments
          </Typography>
          {config.attachments.map((attachment, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ flex: 1 }}>{attachment.name}</Typography>
              <IconButton
                size="small"
                onClick={() => handleAttachmentDelete(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {onDelete && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          sx={{ mt: 2 }}
        >
          Remove Email Configuration
        </Button>
      )}
    </Box>
  );
};

EmailConfig.propTypes = {
  config: PropTypes.shape({
    from_endpoint: PropTypes.string,
    from_name: PropTypes.string,
    reply_to: PropTypes.string,
    priority: PropTypes.oneOf(['high', 'normal', 'low']),
    track_delivery: PropTypes.bool,
    track_replies: PropTypes.bool,
    track_opens: PropTypes.bool,
    track_clicks: PropTypes.bool,
    attachments: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  onConfigChange: PropTypes.func.isRequired,
  communicationEndpoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  onEndpointChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

export default EmailConfig; 